import { withOpacity } from './color.ts';
import type { Rect } from './flex_label.ts';
import type { GraphPoint } from './graph.ts';

/**
 * A value tile's sparkline (tiles spec §3, issue #13): one series as a line, scaled to its own
 * lowest and highest readings, across the box by time. No axes, no labels. Of its own, not the
 * graph's (SP-6), so that each can grow apart. Readings that are all the same give a flat line
 * across the middle; fewer than two, nothing.
 */
export interface Sparkline {
    points: readonly GraphPoint[];
    color: string;
    /** Device pixels, as the graph's line (SP-7). */
    lineWidth: number;
    /** The area below the line, fading from half opaque at the highest reading to clear at the bottom (SP-10). */
    fill?: boolean | undefined;
}

/** How opaque the fill is at the highest reading; it fades to clear at the bottom (the owner). */
const FILL_OPACITY = 0.5;

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Where each point goes in `box`; empty when there is nothing to draw. */
export function sparklinePath(points: readonly GraphPoint[], box: Rect, margin: number) : { x: number; y: number }[] {
    if (points.length < 2) {
        return [];
    }

    let [first, last, min, max] = [Infinity, -Infinity, Infinity, -Infinity];
    for (const { time, value } of points) {
        first = Math.min(first, time);
        last = Math.max(last, time);
        min = Math.min(min, value);
        max = Math.max(max, value);
    }
    const span = last - first;
    if (!(span > 0)) {
        return [];
    }

    const height = Math.max(0, box.height - 2 * margin);
    const range = max - min;

    return points.map(({ time, value }) => ({
        x: box.left + ((time - first) / span) * box.width,
        y: range > 0 ? box.top + margin + height - (height * (value - min)) / range : box.top + box.height / 2,
    }));
}

export function drawSparkline(ctx: Context, box: Rect, sparkline: Sparkline) : void {
    const scale = ctx.getTransform().a;
    const ratio = scale > 0 ? scale : 1;
    // Room for the line at the top and bottom: half its width, and at least the graph's 2 device pixels.
    const path = sparklinePath(sparkline.points, box, Math.max(2, sparkline.lineWidth / 2) / ratio);
    if (path.length === 0) {
        return;
    }

    ctx.save();
    if (sparkline.fill) {
        // Down to the strip's bottom; the gradient runs from there up to the highest reading.
        const bottom = box.top + box.height;
        const highest = Math.min(...path.map(({ y }) => y));
        const gradient = ctx.createLinearGradient(0, bottom, 0, highest);
        gradient.addColorStop(0, withOpacity(sparkline.color, 0));
        gradient.addColorStop(1, withOpacity(sparkline.color, FILL_OPACITY));
        ctx.beginPath();
        ctx.moveTo(path[0]!.x, bottom);
        for (const { x, y } of path) {
            ctx.lineTo(x, y);
        }
        ctx.lineTo(path.at(-1)!.x, bottom);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
    }
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const [index, { x, y }] of path.entries()) {
        if (index === 0) {
            ctx.moveTo(x, y);
        } else {
            ctx.lineTo(x, y);
        }
    }
    // As rgba(): the canvas would read `#aarrggbb` as `#rrggbbaa`.
    ctx.strokeStyle = withOpacity(sparkline.color, 1);
    ctx.lineWidth = sparkline.lineWidth / ratio;
    ctx.stroke();
    ctx.restore();
}
